// ── Touch Look Controller ──
// Converts touch-drag on the right side of the screen into camera deltas,
// replacing Pointer Lock on mobile devices.
//
// F-MOB-H2: the boundary between joystick (left half) and look (right half)
// is configurable. On narrow viewports (< 320px wide) we auto-shift to 0.45
// so the joystick gets less of the screen and can't bleed into the look zone.

import { settingsStore } from '../core/settings';
import { detectDevice } from '../core/device';

const NARROW_VIEWPORT_PX = 320;
const NARROW_VIEWPORT_SPLIT = 0.45; // joystick gets 0..0.45, look gets 0.45..1.

export class TouchLookController {
  /** Accumulated delta since last reset. */
  private dx = 0;
  private dy = 0;

  /** Active touch identifier for the look touch. */
  private activeId: number | null = null;

  /** Previous touch position. */
  private prevX = 0;
  private prevY = 0;

  /** Touch sensitivity (radians per pixel). */
  sensitivity: number;

  /** Minimum viewport-x percentage for the look zone (0-1). F-MOB-H2: now mutable
   *  so settings UI can adjust it; auto-narrows on tiny viewports. */
  private zoneMinX: number;

  /** Whether the user explicitly set zoneMinX via the constructor — if so we
   *  do not auto-adjust on narrow viewports. */
  private readonly explicitZone: boolean;

  /** F-MOB-H3 (chat): when false, the controller ignores all touchstart/move
   *  events and clears any in-flight active touch. Used by the mobile chat
   *  composer overlay so vertical swipes on the soft keyboard don't tug the
   *  camera around. Defaults to true (enabled). */
  private enabled = true;

  // Bound handlers for cleanup
  private readonly handleStart: (e: TouchEvent) => void;
  private readonly handleMove: (e: TouchEvent) => void;
  private readonly handleEnd: (e: TouchEvent) => void;
  private readonly handleVisibilityChange: () => void;
  /** F-MOB-H2: window resize handler so the split shifts when the user
   *  rotates a phone or expands a desktop window. */
  private readonly handleResize: () => void;

  constructor(sensitivity = 0.004, zoneMinX?: number) {
    this.sensitivity = sensitivity;
    this.explicitZone = typeof zoneMinX === 'number';
    if (this.explicitZone) {
      this.zoneMinX = zoneMinX!;
    } else {
      // F-MOB-H2: derive from settingsStore + viewport heuristic.
      this.zoneMinX = TouchLookController.computeZoneMinX();
    }

    this.handleStart = this.onTouchStart.bind(this);
    this.handleMove = this.onTouchMove.bind(this);
    this.handleEnd = this.onTouchEnd.bind(this);
    this.handleVisibilityChange = () => { this.activeId = null; };
    this.handleResize = () => this.refreshZoneMinX();

    // touchstart/touchmove need passive:false because onTouchMove calls preventDefault().
    // touchend/touchcancel never preventDefault — mark passive:true so browsers can
    // optimize scrolling and the listener intent matches its behavior.
    document.addEventListener('touchstart', this.handleStart, { passive: false });
    document.addEventListener('touchmove', this.handleMove, { passive: false });
    document.addEventListener('touchend', this.handleEnd, { passive: true });
    document.addEventListener('touchcancel', this.handleEnd, { passive: true });
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      window.addEventListener('resize', this.handleResize);
    }
  }

  /** BUG-MT-11: gameplay gate. The rotate-overlay only hides via CSS, so any
   *  touch whose target is still inside it (or any pre-game flow before the
   *  body picks up the `in-game` class) must NOT feed camera deltas. Returns
   *  true when this touch event should be ignored entirely. Also short-circuits
   *  on non-mobile devices so accidental wiring on desktop never produces a
   *  jump. */
  private isPreGameOrSuspended(): boolean {
    // Skip when the host page isn't actually in gameplay yet — the rotate
    // overlay's in-game gate is the canonical "ready for input" signal.
    if (typeof document !== 'undefined' && document.body && document.body.classList
        && typeof document.body.classList.contains === 'function'
        && !document.body.classList.contains('in-game')) {
      return true;
    }
    // Belt-and-braces: never accept touch-look on a clearly-non-touch device.
    try {
      if (detectDevice() === 'desktop') return true;
    } catch {
      // detectDevice unavailable in this environment — ignore.
    }
    return false;
  }

  /** BUG-MT-23 + BUG-MT-11: returns true if a touch's target sits inside a
   *  modal/overlay that should suspend the look controller. The primary
   *  mechanism is the `data-suspends-look="true"` attribute that modal panels
   *  opt into; the id/class fallbacks below are a safety net for panels that
   *  haven't been tagged yet (buy menu, scoreboard, vote, map-vote, settings,
   *  rotate overlay). */
  private isSuspendingTarget(target: EventTarget | null): boolean {
    if (!target || typeof (target as Element).closest !== 'function') return false;
    const el = target as Element;
    // Primary: opt-in attribute that modals set on themselves.
    if (el.closest('[data-suspends-look="true"]')) return true;
    // Safety net: rotate-overlay (BUG-MT-11) and generic panel/menu/overlay
    // class fragments. Uses an attribute selector with substring matchers so
    // we don't have to enumerate every panel id.
    if (el.closest('#rotate-overlay')) return true;
    if (el.closest('[class*="panel"], [class*="menu"], [class*="overlay"]')) return true;
    return false;
  }

  private onTouchStart(e: TouchEvent): void {
    if (!this.enabled) return;
    if (this.activeId !== null) return; // already tracking
    // BUG-MT-11: pre-game and non-mobile contexts never drive the camera.
    if (this.isPreGameOrSuspended()) return;

    // MOB-ERG: ignore touches that started on a touch-button or the joystick
    // capture zone — those overlays sit inside the right-side look-zone but
    // are owned by their own controllers. Without this filter, tapping FIRE
    // or SCOPE would also start a camera drag.
    const onActionWidget = (target: EventTarget | null): boolean => {
      if (!target || typeof (target as Element).closest !== 'function') return false;
      const el = target as Element;
      return !!el.closest('#touch-buttons-overlay') || !!el.closest('#touch-joystick-zone');
    };

    const threshold = window.innerWidth * this.zoneMinX;
    for (let i = 0; i < e.changedTouches.length; i++) {
      const t = e.changedTouches[i];
      if (onActionWidget(t.target)) continue;
      // BUG-MT-23 + BUG-MT-11: bail on modal/overlay targets (data-attribute
      // primary, id/class fallbacks for rotate-overlay + panel/menu/overlay).
      if (this.isSuspendingTarget(t.target)) continue;
      if (t.clientX >= threshold) {
        this.activeId = t.identifier;
        this.prevX = t.clientX;
        this.prevY = t.clientY;
        break;
      }
    }
  }

  private onTouchMove(e: TouchEvent): void {
    if (!this.enabled) return;
    if (this.activeId === null) return;
    // BUG-MT-11: if the body lost its in-game class mid-gesture (e.g. the
    // rotate-overlay re-asserted), drop the in-flight touch so the camera
    // doesn't keep accumulating delta while the user can't see the game.
    if (this.isPreGameOrSuspended()) {
      this.activeId = null;
      return;
    }

    for (let i = 0; i < e.changedTouches.length; i++) {
      const t = e.changedTouches[i];
      if (t.identifier === this.activeId) {
        // BUG-MT-23: if the gesture drifted onto a modal/overlay, abandon it
        // rather than emit deltas that would scrub the camera under the UI.
        if (this.isSuspendingTarget(t.target)) {
          this.activeId = null;
          return;
        }
        this.dx += (t.clientX - this.prevX) * this.sensitivity;
        this.dy += (t.clientY - this.prevY) * this.sensitivity;
        this.prevX = t.clientX;
        this.prevY = t.clientY;
        e.preventDefault();
        break;
      }
    }
  }

  private onTouchEnd(e: TouchEvent): void {
    if (this.activeId === null) return;

    // On touchcancel, clear activeId unconditionally — the touch is gone
    if (e.type === 'touchcancel') {
      this.activeId = null;
      return;
    }

    for (let i = 0; i < e.changedTouches.length; i++) {
      if (e.changedTouches[i].identifier === this.activeId) {
        this.activeId = null;
        break;
      }
    }
  }

  /** F-MOB-H2: read the stored split% from settings (default 50) and scale to
   *  a 0..1 zoneMinX, then auto-narrow if the viewport is < NARROW_VIEWPORT_PX
   *  CSS pixels wide so the joystick doesn't bleed into the look zone. */
  private static computeZoneMinX(): number {
    let pct = 50;
    try {
      const stored = settingsStore.get().touchSplitPercent;
      if (typeof stored === 'number' && Number.isFinite(stored)) {
        pct = Math.max(30, Math.min(70, stored));
      }
    } catch {
      // settingsStore unavailable — fall back to default.
    }
    let zone = pct / 100;
    try {
      const w =
        typeof window !== 'undefined' && typeof window.innerWidth === 'number'
          ? window.innerWidth
          : Number.POSITIVE_INFINITY;
      if (w < NARROW_VIEWPORT_PX) {
        zone = NARROW_VIEWPORT_SPLIT;
      }
    } catch {
      // window unavailable — leave zone at the user-configured value.
    }
    return zone;
  }

  /** F-MOB-H2: re-read settings + viewport and update zoneMinX. */
  refreshZoneMinX(): void {
    if (this.explicitZone) return;
    this.zoneMinX = TouchLookController.computeZoneMinX();
  }

  /** F-MOB-H2: read-only accessor for the current look-zone left bound. */
  getZoneMinX(): number {
    return this.zoneMinX;
  }

  /** F-MOB-H3 (chat): pause/resume look-drag handling. While disabled, any
   *  in-flight active touch is dropped and pending dx/dy are zeroed so the
   *  camera doesn't jump on resume. Used by the chat composer + any future
   *  modal overlay that wants exclusive touch focus. */
  setEnabled(enabled: boolean): void {
    this.enabled = !!enabled;
    if (!this.enabled) {
      this.activeId = null;
      this.dx = 0;
      this.dy = 0;
    }
  }

  /** F-MOB-H3 (chat): read-only accessor for the enabled flag. */
  isEnabled(): boolean {
    return this.enabled;
  }

  /** Get accumulated look delta since last reset. */
  getDelta(): { dx: number; dy: number } {
    return { dx: this.dx, dy: this.dy };
  }

  /** Reset accumulated delta (call after consuming). */
  reset(): void {
    this.dx = 0;
    this.dy = 0;
  }

  /** Remove all event listeners. */
  dispose(): void {
    document.removeEventListener('touchstart', this.handleStart);
    document.removeEventListener('touchmove', this.handleMove);
    document.removeEventListener('touchend', this.handleEnd);
    document.removeEventListener('touchcancel', this.handleEnd);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    if (typeof window !== 'undefined' && typeof window.removeEventListener === 'function') {
      window.removeEventListener('resize', this.handleResize);
    }
    this.activeId = null;
    this.dx = 0;
    this.dy = 0;
  }
}
